import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LogOut, UserPlus, Search, FileText, Users, ClipboardCheck, AlertCircle } from "lucide-react";
import SaakhSetuLoader from "@/components/SaakhSetuLoader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const [farmers, setFarmers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewQueue, setReviewQueue] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFarmers();
    // Review queue is Officer/Admin only
    if (user?.role === 'Credit Officer' || user?.role === 'System Administrator') {
      fetchReviewQueue();
    }
  }, [user?.role]);

  const fetchFarmers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/farmers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFarmers(response.data);
    } catch (error) {
      toast.error("Failed to load farmers");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewQueue = async () => {
    try {
      const response = await axios.get(`${API}/assessments/review-queue/pending`);
      setReviewQueue(response.data.assessments || []);
    } catch {
      // silent
    }
  };

  const filteredFarmers = farmers.filter(farmer =>
    farmer.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    farmer.aadhaar_number?.includes(searchTerm) ||
    farmer.contact_number?.includes(searchTerm)
  );

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case "System Administrator": return "bg-yellow-100 text-yellow-800";
      case "Credit Officer": return "bg-blue-100 text-blue-800";
      case "Field Agent": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 600) return "text-green-600";
    if (score >= 500) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3" data-testid="dashboard-brand-header">
              <img
                src="/saakhsetu-logo.png"
                alt="SaakhSetu"
                style={{ height: '36px' }}
                data-testid="dashboard-saakhsetu-logo"
              />
              <span className="text-xs text-gray-400 font-normal hidden sm:inline" style={{ marginTop: '2px' }}>by Arbix AI Solutions</span>
              <span
                className="hidden sm:inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
                style={{ background: '#edf7ea', color: '#3b6e2a', border: '1px solid #c8e0c0', marginLeft: '4px' }}
                data-testid="yogyank-active-badge"
              >
                Yogyank scoring active
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{user.email}</div>
                <Badge className={getRoleBadgeClass(user.role)} data-testid="user-role-badge">{user.role}</Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a202c' }}>Dashboard</h2>
          <p className="text-gray-600">Manage farmer profiles and credit assessments</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="total-farmers-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Farmers</CardTitle>
              <Users className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ color: '#667eea' }}>{farmers.length}</div>
            </CardContent>
          </Card>

          {(user.role === "Field Agent" || user.role === "System Administrator") && (
            <Card className="col-span-1 md:col-span-2">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    className="w-full"
                    onClick={() => navigate("/farmers/new")}
                    data-testid="create-farmer-button"
                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create New Farmer Profile
                  </Button>
                  <Button
                    className="w-full"
                    onClick={() => navigate("/assessment/new")}
                    data-testid="start-assessment-button"
                    style={{ background: '#2563eb' }}
                  >
                    <ClipboardCheck className="h-4 w-4 mr-2" />
                    Start New Assessment
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Review Queue */}
        {reviewQueue.length > 0 && (
          <Card className="mb-6 border-amber-200" data-testid="review-queue-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <CardTitle className="text-sm font-medium">Pending Reviews ({reviewQueue.length})</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {reviewQueue.slice(0, 5).map(a => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between p-2.5 rounded-md border border-amber-100 bg-amber-50/50 cursor-pointer hover:bg-amber-50 transition-colors"
                    onClick={() => navigate(`/assessment/review/${a.id}`)}
                    data-testid={`review-item-${a.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-xs">
                        <div className="font-medium text-gray-800">
                          {a.farmer_profile_snapshot?.farmer_name || 'Unknown'}
                        </div>
                        <div className="text-gray-500">
                          {a.journey_type?.replace(/_/g, ' ')} &middot; Cycle #{a.cycle_number}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold" style={{ color: (a.yogyank_score_base ?? a.yogyank_score_final ?? 0) >= 500 ? '#16a34a' : '#dc2626' }}>
                        {a.yogyank_score_base ?? a.yogyank_score_final ?? 'N/A'}
                      </div>
                      <Badge
                        className={`text-[10px] ${a.assessment_status === 'SUBMITTED' ? 'bg-purple-100 text-purple-800' : 'bg-amber-100 text-amber-800'}`}
                        data-testid={`review-status-${a.id}`}
                      >
                        {a.assessment_status === 'SUBMITTED' ? 'Awaiting Score' : 'Pending Review'}
                      </Badge>
                    </div>
                  </div>
                ))}
                {reviewQueue.length > 5 && (
                  <div className="text-xs text-center text-gray-400 pt-1">+{reviewQueue.length - 5} more pending</div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card data-testid="farmers-list-card">
          <CardHeader>
            <CardTitle>Farmer Profiles</CardTitle>
            <CardDescription>Search and view all registered farmers</CardDescription>
            <div className="pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, Aadhaar, or contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-farmer-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <SaakhSetuLoader message="Loading farmers..." />
              </div>
            ) : filteredFarmers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? "No farmers found matching your search" : "No farmers registered yet"}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFarmers.map((farmer) => (
                  <div
                    key={farmer.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow"
                    data-testid={`farmer-card-${farmer.id}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg" style={{ color: '#1a202c' }}>{farmer.farmer_name}</h3>
                      <div className="text-sm text-gray-600 mt-1">
                        <span>Aadhaar: ****{farmer.aadhaar_number?.slice(-4)}</span>
                        <span className="mx-2">•</span>
                        <span>Contact: {farmer.contact_number}</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <span>{farmer.total_land_holding_size_acres} acres</span>
                        {farmer.village_id && (
                          <>
                            <span className="mx-2">•</span>
                            <span>{farmer.state_id}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {/* Credit Score - visible only to Credit Officer and System Admin */}
                      {(user.role === "Credit Officer" || user.role === "System Administrator") && (
                        <div className="text-center">
                          <div className="text-sm text-gray-500">Entitlement Score</div>
                          {(farmer.yogyank_score_base ?? farmer.yogyank_score_final) != null ? (
                            <div className={`text-2xl font-bold ${getScoreColor(farmer.yogyank_score_base ?? farmer.yogyank_score_final)}`}>
                              {farmer.yogyank_score_base ?? farmer.yogyank_score_final}
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-gray-300">&mdash;</div>
                          )}
                        </div>
                      )}
                      <div className="flex space-x-2">
                        {(user.role === "Field Agent" || user.role === "System Administrator") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/farmers/edit/${farmer.id}`)}
                            data-testid={`edit-farmer-button-${farmer.id}`}
                          >
                            Edit
                          </Button>
                        )}
                        {(user.role === "Credit Officer" || user.role === "System Administrator") && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/farmers/${farmer.id}/report`)}
                            data-testid={`view-report-button-${farmer.id}`}
                            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Report
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;